import { useMemo } from 'react';

import { useSession } from '@ais-ui/websockets';
import { UseQueryResult } from '@tanstack/react-query';
import { useCredentials } from '@ais-ui/auth';
import { useBasePath } from '@ais-ui/custom-hooks';
import { useTrackedQuery as useQuery } from '@ais-ui/performance-tracking';
import {
  Configuration,
  DefinitionGet200Response,
  WatchdogApi,
} from '@ais-ui/api-spec/watchdog';
import { getBasePath } from './getAISBasePath';
import { getAuthHeaderFromCredentials } from '@ais-ui/utils';

const useWatchdogApi = () => {
  const credentials = useCredentials();
  const { sessionId } = useSession();
  const { hostname } = useBasePath();

  let basePath = getBasePath();
  let configuration: Configuration;
  if (hostname.includes('localhost')) {
    const username = credentials?.username || '';
    const password = credentials?.password || '';

    const authHeader = getAuthHeaderFromCredentials(username, password);
    configuration = new Configuration({
      basePath: `${basePath}api/watchdog`,
      headers: {
        'X-Application-Name': 'marinepulse',
        Authorization: authHeader,
      },
    });
  } else {
    basePath = `${basePath}aisweb/api/watchdog`;
    configuration = new Configuration({
      basePath: basePath,
      headers: {
        'Aisweb-Session': sessionId,
        'X-Application-Name': 'marinepulse',
      },
    });
  }

  const watchdogApi = useMemo(
    () => new WatchdogApi(configuration),
    [configuration],
  );
  return watchdogApi;
};

export const useFetchAllWatchdogDefinitions =
  (): UseQueryResult<DefinitionGet200Response> => {
    const watchdogApi = useWatchdogApi();
    return useQuery(
      {
        queryKey: ['watchdogAllDefinitions'],
        queryFn: async () => {
          return await watchdogApi.definitionGet({ useUid: true });
        },
      },
      'watchdog:listDefinitions',
    );
  };

export const useConditionalWatchdogDefinitionsFetch = (
  shouldFetch: boolean,
): UseQueryResult<DefinitionGet200Response, Error> => {
  const watchdogApi = useWatchdogApi();

  return useQuery(
    {
      queryKey: ['watchdogAllDefinitions'],
      queryFn: async () => {
        if (shouldFetch) {
          return await watchdogApi.definitionGet({ useUid: true });
        } else {
          // Return a default value when shouldFetch is false
          return Promise.resolve({ records: [] } as DefinitionGet200Response);
        }
      },
    },
    'watchdog:conditionalDefinitions',
  );
};
